import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { useMediaQuery } from '../hooks/useMediaQuery';

/**
 * Custom cursor — smooth dot + ring follow.
 * Inspired by ko-yelie's cursor design.
 * - Small dot at cursor position
 * - Larger ring that follows with lerp smoothing
 * - Violet + lime accent colors
 * - Disabled on touch devices, mobile, reduced-motion users
 */
const CursorTrail = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    // Disable on touch devices, mobile, or reduced motion
    if (prefersReducedMotion || isTouchDevice || isMobile) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let ringX = mouseX;
    let ringY = mouseY;
    let rafId;
    let isVisible = false;

    const onMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (!isVisible) {
        isVisible = true;
        dot.style.opacity = '1';
        ring.style.opacity = '1';
      }

      // Dot follows immediately
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px) translate(-50%, -50%)`;
    };

    const onMouseLeave = () => {
      isVisible = false;
      dot.style.opacity = '0';
      ring.style.opacity = '0';
    };

    const onMouseDown = () => {
      ring.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%) scale(0.6)`;
    };

    const onMouseUp = () => {
      ring.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%) scale(1)`;
    };

    const animate = () => {
      // Ring follows with lerp smoothing
      ringX += (mouseX - ringX) * 0.15;
      ringY += (mouseY - ringY) * 0.15;
      ring.style.transform = `translate(${ringX}px, ${ringY}px) translate(-50%, -50%)`;

      rafId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [prefersReducedMotion, isTouchDevice, isMobile]);

  // Don't render on touch/mobile/reduced-motion
  if (prefersReducedMotion || isTouchDevice || isMobile) return null;

  return (
    <>
      {/* Dot — follows cursor exactly */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 w-2 h-2 rounded-full bg-accent pointer-events-none z-[9999] opacity-0 transition-opacity duration-200"
        style={{ transform: 'translate(-100px, -100px)' }}
      />
      {/* Ring — follows with smoothing */}
      <div
        ref={ringRef}
        className="fixed top-0 left-0 w-8 h-8 rounded-full border border-accent/50 pointer-events-none z-[9999] opacity-0 transition-opacity duration-200"
        style={{ transform: 'translate(-100px, -100px)' }}
      />
    </>
  );
};

export default CursorTrail;